/**
 * 远程输入协议 v2 协商状态管理。
 *
 * 职责：
 *   - 解析 receiver 发回的 control 通道消息（首要是 `hello`），记录其声明的 capability；
 *   - 提供 capability gating 查询：sender 在发送可选消息（wheel / relativeMouse / clipboard）
 *     之前**必须**先确认双方都已声明对应 capability（协议 §5.1）；
 *   - 提供 receiver hello 是否已到达的状态，供"hello 之前可选业务消息丢弃"的策略判断。
 *
 * 设计取舍：
 *   - 单例会话级状态（与 WebRTCConnector 单例语义一致），data channel close / connector destroy
 *     时调用 `resetNegotiationState` 复位，避免跨会话状态污染；
 *   - sender 自身已实现并声明的 capability 由 sender hello 决定（见 keyboard.ts `sendHello`），
 *     此处仅记录 receiver 端，gating = sender 自身实现 ∩ receiver 声明。
 */

type ControlMessageType =
	| "hello"
	| "keyDown"
	| "keyUp"
	| "textInput"
	| "mode"
	| "releaseAll"
	| "clipboard:set"
	| "clipboard:request"
	| string;

type ParsedMessage = {
	v?: unknown;
	type?: ControlMessageType;
	side?: unknown;
	capabilities?: unknown;
	keyboardLayout?: unknown;
};

/**
 * sender 自身已实现并通过 hello 宣告的 capability（必须与 keyboard.ts `sendHello` 同步）。
 *
 * gating 时取 sender 与 receiver 双方声明的交集，确保不会因 sender 单边声明而误发可选消息。
 */
const SENDER_CAPABILITIES: ReadonlySet<string> = new Set([
	"scancode",
	"unicode",
	"mode",
	"wheel",
	"relativeMouse",
	"clipboardText",
]);

let receiverHelloReceived = false;
let receiverCapabilities: ReadonlySet<string> = new Set();

/**
 * 复位协商状态。应当在每次 peer connection / data channel 重建时调用。
 */
export const resetNegotiationState = () => {
	receiverHelloReceived = false;
	receiverCapabilities = new Set();
};

/**
 * 判断双方是否都声明了某个 capability。
 *
 * 在 receiver hello 到达之前返回 false——按协议 §5：业务消息**应当**缓冲或丢弃。
 * 对于可选消息（wheel / relativeMouse / clipboard），这意味着在协商完成前不发送。
 * @param name capability 名称
 */
export const hasNegotiatedCapability = (name: string): boolean => {
	if (!receiverHelloReceived) return false;
	if (!SENDER_CAPABILITIES.has(name)) return false;
	return receiverCapabilities.has(name);
};

/**
 * 是否已收到 receiver 的 hello。
 */
export const isReceiverHelloReceived = (): boolean => receiverHelloReceived;

/**
 * 安全地把未知 JSON 解析为协议消息形态。
 */
const parseRaw = (raw: unknown): ParsedMessage | null => {
	if (typeof raw !== "string") return null;
	try {
		const parsed = JSON.parse(raw) as ParsedMessage;
		if (!parsed || typeof parsed !== "object") return null;
		return parsed;
	} catch {
		return null;
	}
};

/**
 * 在 control 通道 onmessage 中调用。
 *
 * 当前仅识别 `hello`，其它类型留给业务模块自行处理（剪贴板 / 错误回执等后续可补）。
 * @param event control 通道 MessageEvent
 */
export const handleControlMessage = (event: MessageEvent): void => {
	const parsed = parseRaw(event.data);
	if (!parsed) return;
	if (parsed.v !== 2) {
		// 协议 §12.1：v !== 2 一律丢弃 + warn。也包括 v1 receiver 完全不发 hello 的情况——
		// 这种情况下 receiverHelloReceived 永远为 false，可选消息也将自动 gated 掉。
		console.warn(
			"[negotiation] discarded non-v2 control message:",
			parsed.type,
			"v=",
			parsed.v,
		);
		return;
	}
	if (parsed.type !== "hello") {
		// 后续 minor 可在此扩展（例如 clipboard:set 由 receiver 推送）。
		return;
	}
	if (parsed.side !== "receiver") {
		// sender 自己的 hello 不应被回环消费，receiver 之外的 side 直接忽略
		return;
	}
	const caps = Array.isArray(parsed.capabilities)
		? new Set(
				parsed.capabilities.filter(
					(item): item is string => typeof item === "string",
				),
			)
		: new Set<string>();
	receiverCapabilities = caps;
	receiverHelloReceived = true;
	console.log("[negotiation] receiver hello accepted:", {
		capabilities: Array.from(caps),
	});
};
